import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Link, Outlet } from '@tanstack/react-router'
import { AlertTriangle, RotateCw } from 'lucide-react'
import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import { recoverChunkLoad } from '@/lib/chunk-recovery'

export type RouterContext = {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: ErrorPage,
  notFoundComponent: NotFound,
})

/*
  纯色底。原来这里铺着三层 radial-gradient 环境光（青 右上 / 紫 左下 / 蓝 居中），
  随 ignix v8 一起去掉：主色由青转粉之后，青紫蓝的环境光与品牌色直接打架，
  而且它是按浅底调的 alpha，暗色主题下要么看不见要么变成一团脏光。
  层次改由 --void / --night / --panel 三档表面自己承担。
*/
function RootLayout() {
  return (
    <div className="min-h-screen bg-void text-txt antialiased">
      <Outlet />
    </div>
  )
}

/**
 * 全局错误边界。
 *
 * 没有它的话，任何一处渲染异常都会让整个 SPA 白屏——用户看到的是空白页，
 * 既不知道发生了什么，也没有恢复路径，只能自己想到刷新。
 *
 * 错误详情只在开发态展示：生产环境把堆栈摆在页面上，等于把内部结构
 * 主动告诉每一个访问者。
 */
function ErrorPage({ error }: { error: Error; reset: () => void }) {
  const isDev = import.meta.env.DEV

  useEffect(() => {
    recoverChunkLoad(error)
  }, [error])

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="rise w-full max-w-md text-center">
        <div className="surface mx-auto flex size-14 items-center justify-center rounded-apple-lg">
          <AlertTriangle className="size-6 text-warn" />
        </div>

        <h1 className="display-title mt-5 text-[26px] leading-tight text-txt">页面出错了</h1>
        <p className="mt-2.5 text-[13.5px] leading-relaxed text-mut">
          这一部分没能正常加载。可以先重试；如果反复出现，请把当前页面地址反馈给技术同学。
        </p>

        {isDev && (
          <pre className="surface-inset mt-5 max-h-48 overflow-auto p-3.5 text-left font-mono text-[11px] leading-relaxed text-mut">
            {error.message}
            {error.stack && `\n\n${error.stack.split('\n').slice(1, 6).join('\n')}`}
          </pre>
        )}

        <div className="mt-6 flex items-center justify-center gap-2.5">
          <Button
            className="pressable h-9 gap-1.5 rounded-apple text-[13px]"
            onClick={() => window.location.reload()}
          >
            <RotateCw className="size-3.5" />
            重试
          </Button>
          <Button
            variant="outline"
            className="pressable h-9 rounded-apple text-[13px]"
            onClick={() => {
              window.location.href = '/dashboard'
            }}
          >
            返回概览
          </Button>
        </div>
      </div>
    </div>
  )
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="rise text-center">
        <p className="font-mono text-[12px] tracking-[0.2em] text-dim">404</p>
        <h1 className="display-title mt-3 text-[30px] leading-none text-txt">页面不存在</h1>
        <p className="mt-3 text-[13.5px] text-mut">这个地址没有对应的页面。</p>
        <Link
          to="/dashboard"
          className="mt-5 inline-block text-[13.5px] text-cyan hover:underline"
        >
          返回概览
        </Link>
      </div>
    </div>
  )
}
